import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './components/LanguageSwitcher';
import CookieBanner from './CookieBanner';

function Landing() {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [legalModal, setLegalModal] = useState(null);

  const scrollTo = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMenuOpen(false);
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const res = await fetch('/send-email.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name, email: form.email, message: `Teléfono: ${form.phone || 'No indicado'}\n\n${form.message}` }),
      });
      const data = await res.json();
      if (data.ok) {
        setSubmitStatus('success');
        setForm({ name: '', email: '', phone: '', message: '' });
        setTimeout(() => setShowForm(false), 1500);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formOverlayStyle = {
    position: 'fixed', inset: 0, zIndex: 200,
    background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 24, overflowY: 'auto',
  };

  const formCardStyle = {
    background: '#0f1117', border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 20, padding: 40, width: '100%', maxWidth: 420,
    position: 'relative',
    margin: '0 auto',
    boxSizing: 'border-box',
  };

  const inputStyle = {
    width: '100%', padding: '12px 16px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.07)',
    background: '#080910', color: '#f0f0f5', fontFamily: 'Inter, sans-serif',
    fontSize: 16, outline: 'none', boxSizing: 'border-box',
  };

  /* ── Matrix Canvas ── */
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const chars = 'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン0123456789ABCDEF<>{}()[]'.split('');
    const fontSize = 13;
    let cols, drops;

    function resize() {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      cols = Math.floor(canvas.width / fontSize);
      drops = Array(cols).fill(1);
    }
    resize();
    window.addEventListener('resize', resize);

    let timer = setInterval(() => {
      ctx.fillStyle = 'rgba(8,9,16,0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.font = `${fontSize}px monospace`;
      drops.forEach((y, i) => {
        const char = chars[Math.floor(Math.random() * chars.length)];
        const progress = y / (canvas.height / fontSize);
        const r = Math.round(124 + (6 - 124) * progress);
        const g = Math.round(58 + (182 - 58) * progress);
        const b = Math.round(237 + (212 - 237) * progress);
        ctx.fillStyle = `rgb(${r},${g},${b})`;
        ctx.fillText(char, i * fontSize, y * fontSize);
        if (y * fontSize > canvas.height && Math.random() > 0.975) drops[i] = 0;
        drops[i]++;
      });
    }, 50);

    return () => {
      clearInterval(timer);
      window.removeEventListener('resize', resize);
    };
  }, []);

  /* ── Typewriter ── */
  const words = t('home.typewriter', { returnObjects: true });
  const [typedText] = useState(Array.isArray(words) ? words[0] : 'máquina.');

  useEffect(() => {
    const w = Array.isArray(words) ? words : ['máquina.', 'velocidad.', 'resultados.', 'futuro.', 'IA.'];
    if (w.length === 0) return;
    const el = document.getElementById('typed-word');
    if (!el) return;
    let wi = 0, ci = 0, deleting = false, timer;

    function type() {
      const word = w[wi];
      if (!deleting) {
        el.textContent = word.slice(0, ++ci);
        if (ci === word.length) { deleting = true; timer = setTimeout(type, 1800); return; }
        timer = setTimeout(type, 95);
      } else {
        el.textContent = word.slice(0, --ci);
        if (ci === 0) { deleting = false; wi = (wi + 1) % w.length; timer = setTimeout(type, 300); return; }
        timer = setTimeout(type, 45);
      }
    }

    const start = setTimeout(type, 800);
    return () => { clearTimeout(start); clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [words]);

  /* ── Scroll Reveal ── */
  useEffect(() => {
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('visible'); io.unobserve(e.target); }
      });
    }, { threshold: 0.12 });
    document.querySelectorAll('.reveal').forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);


  /* ── Active Nav Link ── */
  useEffect(() => {
    const sections = document.querySelectorAll('section[id], div[id]');
    const navLinks = document.querySelectorAll('.nav-links a');
    function onScroll() {
      let current = '';
      sections.forEach(sec => { if (window.scrollY >= sec.offsetTop - 100) current = sec.id; });
      navLinks.forEach(a => { a.style.color = a.getAttribute('href') === '#' + current ? '#f0f0f5' : ''; });
    }
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* ── Hero entrance ── */
  useEffect(() => {
    const heroReveals = document.querySelectorAll('#hero .reveal');
    heroReveals.forEach((el, i) => {
      setTimeout(() => el.classList.add('visible'), 400 + i * 150);
    });
  }, []);

  return (
    <>
      <canvas ref={canvasRef} id="matrix-canvas" />
      <div className="glow"></div>

      <nav>
        <div className="nav-inner">
          <a href="/" className="logo" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>elmakinatech</a>
          <ul className="nav-links">
            <li><a onClick={(e) => scrollTo(e, 'servicios')} href="#servicios">{t('home.nav.services')}</a></li>
            <li><a onClick={(e) => scrollTo(e, 'como')} href="#como">{t('home.nav.how')}</a></li>
            <li><a onClick={(e) => scrollTo(e, 'por-que')} href="#por-que">{t('home.nav.why')}</a></li>
            <li><LanguageSwitcher /></li>
            <li><button type="button" onClick={() => setShowForm(true)} className="nav-cta" style={{ border: 'none', cursor: 'pointer' }}>{t('home.nav.cta')}</button></li>
          </ul>
          <button className="hamburger" onClick={() => setMenuOpen(!menuOpen)} aria-label={t('home.nav.menu')}>
            <span></span><span></span><span></span>
          </button>
        </div>
      </nav>

      <div className={`mobile-menu${menuOpen ? ' open' : ''}`}>
        <a onClick={(e) => scrollTo(e, 'servicios')} href="#servicios">{t('home.nav.services')}</a>
        <a onClick={(e) => scrollTo(e, 'como')} href="#como">{t('home.nav.how')}</a>
        <a onClick={(e) => scrollTo(e, 'por-que')} href="#por-que">{t('home.nav.why')}</a>
        <a onClick={() => { setMenuOpen(false); setShowForm(true); }} href="#cta" style={{ color: '#a78bfa', fontWeight: 700 }}>{t('home.nav.cta')}</a>
      </div>

      {/* FLOATING FORM */}
      {showForm && (
        <div style={formOverlayStyle} onClick={(e) => { if (e.target === e.currentTarget) setShowForm(false); }}>
          <div style={formCardStyle}>
            <button
              onClick={() => setShowForm(false)}
              style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}
              aria-label={t('home.form.close')}
            >✕</button>
            <h3 style={{ fontFamily: 'Inter, sans-serif', fontSize: 24, fontWeight: 800, color: '#f0f0f5', marginBottom: 8 }}>{t('home.form.title')}</h3>
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: '#6b7280', marginBottom: 28, lineHeight: 1.6 }}>
              {t('home.form.desc')}
            </p>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <input style={inputStyle} type="text" name="name" placeholder={t('home.form.name')} value={form.name} onChange={handleChange} required />
              <input style={inputStyle} type="email" name="email" placeholder={t('home.form.email')} value={form.email} onChange={handleChange} required />
              <input style={inputStyle} type="tel" name="phone" placeholder={t('home.form.phone')} value={form.phone} onChange={handleChange} />
              <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }} name="message" placeholder={t('home.form.message')} value={form.message} onChange={handleChange} />
              {submitStatus === 'success' && <p style={{ color: '#22c55e', fontSize: 13, textAlign: 'center' }}>{t('home.form.success')}</p>}
              {submitStatus === 'error' && <p style={{ color: '#ef4444', fontSize: 13, textAlign: 'center' }}>{t('home.form.error')}</p>}
              <button type="submit" disabled={isSubmitting} style={{
                padding: '14px 36px',
                background: 'linear-gradient(135deg, #7c3aed, #06b6d4)',
                color: '#fff', fontWeight: 700, fontSize: 15, fontFamily: 'Inter, sans-serif',
                border: 'none', borderRadius: 10, cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.6 : 1,
              }}>{isSubmitting ? t('home.form.sending') : t('home.form.submit')}</button>
            </form>
          </div>
        </div>
      )}

      {/* LEGAL MODAL */}
      {legalModal && (
        <div style={formOverlayStyle} onClick={(e) => { if (e.target === e.currentTarget) setLegalModal(null); }}>
          <div style={{ ...formCardStyle, maxWidth: 560, maxHeight: '80vh', overflowY: 'auto', padding: 32 }}>
            <button
              onClick={() => setLegalModal(null)}
              style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 20, lineHeight: 1, zIndex: 1 }}
              aria-label="Cerrar"
            >✕</button>
            {legalModal === 'privacidad' && (
              <>
                <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 800, color: '#f0f0f5', marginBottom: 16 }}>{t('legal.privacidad.title')}</h3>
                <div style={{ fontSize: 14, color: '#d1d5db', lineHeight: 1.7 }}>
                  <p style={{ marginBottom: 16 }}>{t('legal.privacidad.intro')}</p>
                  <h4 style={{ color: '#f0f0f5', fontWeight: 700, marginBottom: 8, marginTop: 16 }}>{t('legal.privacidad.who.title')}</h4>
                  <p style={{ marginBottom: 12 }}>{t('legal.privacidad.who.body')}</p>
                  <h4 style={{ color: '#f0f0f5', fontWeight: 700, marginBottom: 8, marginTop: 16 }}>{t('legal.privacidad.data.title')}</h4>
                  <p style={{ marginBottom: 12 }}>{t('legal.privacidad.data.body')}</p>
                  <h4 style={{ color: '#f0f0f5', fontWeight: 700, marginBottom: 8, marginTop: 16 }}>{t('legal.privacidad.purpose.title')}</h4>
                  <ul style={{ paddingLeft: 20, marginBottom: 12 }}>
                    {t('legal.privacidad.purpose.items', { returnObjects: true }).map((item, i) => <li key={i} style={{ marginBottom: 4 }}>{item}</li>)}
                  </ul>
                  <h4 style={{ color: '#f0f0f5', fontWeight: 700, marginBottom: 8, marginTop: 16 }}>{t('legal.privacidad.legal.title')}</h4>
                  <p style={{ marginBottom: 12 }}>{t('legal.privacidad.legal.body')}</p>
                  <h4 style={{ color: '#f0f0f5', fontWeight: 700, marginBottom: 8, marginTop: 16 }}>{t('legal.privacidad.retention.title')}</h4>
                  <p style={{ marginBottom: 12 }}>{t('legal.privacidad.retention.body')}</p>
                  <h4 style={{ color: '#f0f0f5', fontWeight: 700, marginBottom: 8, marginTop: 16 }}>{t('legal.privacidad.rights.title')}</h4>
                  <p style={{ marginBottom: 8 }}>{t('legal.privacidad.rights.body')}</p>
                  <ul style={{ paddingLeft: 20, marginBottom: 12 }}>
                    {t('legal.privacidad.rights.items', { returnObjects: true }).map((item, i) => <li key={i} style={{ marginBottom: 4 }}>{item}</li>)}
                  </ul>
                  <h4 style={{ color: '#f0f0f5', fontWeight: 700, marginBottom: 8, marginTop: 16 }}>{t('legal.privacidad.third.title')}</h4>
                  <p style={{ marginBottom: 12 }}>{t('legal.privacidad.third.body')}</p>
                  <h4 style={{ color: '#f0f0f5', fontWeight: 700, marginBottom: 8, marginTop: 16 }}>{t('legal.privacidad.cookies.title')}</h4>
                  <p style={{ marginBottom: 12 }}>{t('legal.privacidad.cookies.body')}</p>
                  <h4 style={{ color: '#f0f0f5', fontWeight: 700, marginBottom: 8, marginTop: 16 }}>{t('legal.privacidad.changes.title')}</h4>
                  <p style={{ marginBottom: 12 }}>{t('legal.privacidad.changes.body')}</p>
                </div>
              </>
            )}
            {legalModal === 'terminos' && (
              <>
                <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 800, color: '#f0f0f5', marginBottom: 16 }}>{t('legal.terms.title')}</h3>
                <div style={{ fontSize: 14, color: '#d1d5db', lineHeight: 1.7 }}>
                  <p style={{ marginBottom: 16 }}>{t('legal.terms.intro')}</p>
                  <h4 style={{ color: '#f0f0f5', fontWeight: 700, marginBottom: 8, marginTop: 16 }}>{t('legal.terms.service.title')}</h4>
                  <p style={{ marginBottom: 12 }}>{t('legal.terms.service.body')}</p>
                  <h4 style={{ color: '#f0f0f5', fontWeight: 700, marginBottom: 8, marginTop: 16 }}>{t('legal.terms.use.title')}</h4>
                  <p style={{ marginBottom: 8 }}>{t('legal.terms.use.body')}</p>
                  <ul style={{ paddingLeft: 20, marginBottom: 12 }}>
                    {t('legal.terms.use.items', { returnObjects: true }).map((item, i) => <li key={i} style={{ marginBottom: 4 }}>{item}</li>)}
                  </ul>
                  <h4 style={{ color: '#f0f0f5', fontWeight: 700, marginBottom: 8, marginTop: 16 }}>{t('legal.terms.propiedad.title')}</h4>
                  <p style={{ marginBottom: 12 }}>{t('legal.terms.propiedad.body')}</p>
                  <h4 style={{ color: '#f0f0f5', fontWeight: 700, marginBottom: 8, marginTop: 16 }}>{t('legal.terms.responsabilidad.title')}</h4>
                  <p style={{ marginBottom: 12 }}>{t('legal.terms.responsabilidad.body')}</p>
                  <h4 style={{ color: '#f0f0f5', fontWeight: 700, marginBottom: 8, marginTop: 16 }}>{t('legal.terms.termination.title')}</h4>
                  <p style={{ marginBottom: 12 }}>{t('legal.terms.termination.body')}</p>
                  <h4 style={{ color: '#f0f0f5', fontWeight: 700, marginBottom: 8, marginTop: 16 }}>{t('legal.terms.law.title')}</h4>
                  <p style={{ marginBottom: 12 }}>{t('legal.terms.law.body')}</p>
                  <h4 style={{ color: '#f0f0f5', fontWeight: 700, marginBottom: 8, marginTop: 16 }}>{t('legal.terms.contact.title')}</h4>
                  <p style={{ marginBottom: 12 }}>{t('legal.terms.contact.body')}</p>
                </div>
              </>
            )}
            {legalModal === 'cookies' && (
              <>
                <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 800, color: '#f0f0f5', marginBottom: 16 }}>{t('legal.cookies.title')}</h3>
                <div style={{ fontSize: 14, color: '#d1d5db', lineHeight: 1.7 }}>
                  <p style={{ marginBottom: 16 }}>{t('legal.cookies.intro')}</p>
                  <h4 style={{ color: '#f0f0f5', fontWeight: 700, marginBottom: 8, marginTop: 16 }}>{t('legal.cookies.what.title')}</h4>
                  <p style={{ marginBottom: 12 }}>{t('legal.cookies.what.body')}</p>
                  <h4 style={{ color: '#f0f0f5', fontWeight: 700, marginBottom: 8, marginTop: 16 }}>{t('legal.cookies.types.title')}</h4>
                  <div style={{ marginBottom: 12 }}>
                    {t('legal.cookies.types.items', { returnObjects: true }).map((type, i) => (
                      <div key={i} style={{ background: '#0f1117', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 8, padding: 12, marginBottom: 8 }}>
                        <p style={{ color: '#f0f0f5', fontWeight: 700, marginBottom: 4, fontSize: 13 }}>{type.name}</p>
                        <p style={{ color: '#9ca3af', fontSize: 13 }}>{type.desc}</p>
                      </div>
                    ))}
                  </div>
                  <h4 style={{ color: '#f0f0f5', fontWeight: 700, marginBottom: 8, marginTop: 16 }}>{t('legal.cookies.google.title')}</h4>
                  <p style={{ marginBottom: 12 }}>{t('legal.cookies.google.body')}</p>
                  <h4 style={{ color: '#f0f0f5', fontWeight: 700, marginBottom: 8, marginTop: 16 }}>{t('legal.cookies.third.title')}</h4>
                  <p style={{ marginBottom: 12 }}>{t('legal.cookies.third.body')}</p>
                  <h4 style={{ color: '#f0f0f5', fontWeight: 700, marginBottom: 8, marginTop: 16 }}>{t('legal.cookies.manage.title')}</h4>
                  <p style={{ marginBottom: 8 }}>{t('legal.cookies.manage.body')}</p>
                  <ul style={{ paddingLeft: 20, marginBottom: 12 }}>
                    {t('legal.cookies.manage.items', { returnObjects: true }).map((item, i) => <li key={i} style={{ marginBottom: 4 }}>{item}</li>)}
                  </ul>
                  <h4 style={{ color: '#f0f0f5', fontWeight: 700, marginBottom: 8, marginTop: 16 }}>{t('legal.cookies.changes.title')}</h4>
                  <p style={{ marginBottom: 12 }}>{t('legal.cookies.changes.body')}</p>
                </div>
              </>
            )}
            {legalModal === 'contacto' && (
              <>
                <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 800, color: '#f0f0f5', marginBottom: 4 }}>{t('contact.title')}</h3>
                <p style={{ fontSize: 14, color: '#9ca3af', marginBottom: 20 }}>{t('contact.description')}</p>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <input style={inputStyle} type="text" name="name" placeholder={t('contact.form.namePlaceholder')} value={form.name} onChange={handleChange} required />
                  <input style={inputStyle} type="email" name="email" placeholder={t('contact.form.emailPlaceholder')} value={form.email} onChange={handleChange} required />
                  <input style={inputStyle} type="tel" name="phone" placeholder={t('contact.form.phonePlaceholder')} value={form.phone} onChange={handleChange} />
                  <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }} name="message" placeholder={t('contact.form.messagePlaceholder')} value={form.message} onChange={handleChange} required />
                  {submitStatus === 'success' && <p style={{ color: '#22c55e', fontSize: 13, textAlign: 'center' }}>{t('contact.form.success')}</p>}
                  {submitStatus === 'error' && <p style={{ color: '#ef4444', fontSize: 13, textAlign: 'center' }}>{t('contact.form.error')}</p>}
                  <button type="submit" disabled={isSubmitting} style={{
                    padding: '14px 36px',
                    background: 'linear-gradient(135deg, #7c3aed, #06b6d4)',
                    color: '#fff', fontWeight: 700, fontSize: 15, fontFamily: 'Inter, sans-serif',
                    border: 'none', borderRadius: 10, cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    opacity: isSubmitting ? 0.6 : 1,
                  }}>{isSubmitting ? t('contact.form.sending') : t('contact.form.send')}</button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* HERO */}
      <section id="hero">
        <div className="badge">{t('home.hero.badge')}</div>
        <h1>{t('home.hero.heading')}<br /><span id="typed-word" className="grad typed-word">{typedText}</span></h1>
        <p className="hero-sub reveal">{t('home.hero.subtitle')}</p>
        <div className="hero-actions reveal reveal-delay-1">
          <button type="button" onClick={() => setShowForm(true)} className="btn-primary" style={{ border: 'none', cursor: 'pointer' }}>
            {t('home.hero.start')}
          </button>
          <a href="#servicios" onClick={(e) => scrollTo(e, 'servicios')} className="btn-secondary">{t('home.hero.viewServices')}</a>
        </div>

      </section>

      {/* SERVICES */}
      <section id="servicios">
        <div className="section-header">
          <div className="section-tag">{t('home.services.tag')}</div>
          <h2 dangerouslySetInnerHTML={{ __html: t('home.services.title') }} />
          <p className="section-sub">{t('home.services.subtitle')}</p>
        </div>

        <div className="services-grid">
          <div className="card reveal">
            <div className="card-icon">🤖</div>
            <h3>{t('home.services.ai.title')}</h3>
            <p>{t('home.services.ai.desc')}</p>
            <div className="card-tags">
              {t('home.services.ai.tags', { returnObjects: true }).map((tag, i) => (
                <span key={i} className="tag">{tag}</span>
              ))}
            </div>
          </div>

          <div className="card reveal reveal-delay-2">
            <div className="card-icon">🎬</div>
            <h3>{t('home.services.ugc.title')}</h3>
            <p>{t('home.services.ugc.desc')}</p>
            <div className="card-tags">
              {t('home.services.ugc.tags', { returnObjects: true }).map((tag, i) => (
                <span key={i} className="tag">{tag}</span>
              ))}
            </div>
          </div>

          <div className="card reveal reveal-delay-3">
            <div className="card-icon">🚀</div>
            <h3>{t('home.services.saas.title')}</h3>
            <p>{t('home.services.saas.desc')}</p>
            <div className="card-tags">
              {t('home.services.saas.tags', { returnObjects: true }).map((tag, i) => (
                <span key={i} className="tag">{tag}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <div id="como" style={{ padding: '0 0 100px', maxWidth: 'var(--max)', margin: '0 auto' }}>
        <div id="how">
          <div className="section-header">
            <div className="section-tag">{t('home.process.tag')}</div>
            <h2 dangerouslySetInnerHTML={{ __html: t('home.process.title') }} />
          </div>
          <div className="steps">
            {t('home.process.steps', { returnObjects: true }).map((step, i) => (
              <div key={i} className="step">
                <div className="step-num">{i + 1}</div>
                <h4>{step.title}</h4>
                <p>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* WHY */}
      <section id="por-que">
        <div className="section-header">
          <div className="section-tag">{t('home.why.tag')}</div>
          <h2 dangerouslySetInnerHTML={{ __html: t('home.why.title') }} />
        </div>

        <div className="why-grid">
          {t('home.why.items', { returnObjects: true }).map((item, i) => (
            <div key={i} className="why-item">
              <div className="why-icon">{['⚡', '🎯', '🧠', '🤝'][i]}</div>
              <div>
                <h4>{item.title}</h4>
                <p>{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section id="cta">
        <div className="cta-box">
          <div className="badge">{t('home.cta.badge')}</div>
          <h2 dangerouslySetInnerHTML={{ __html: t('home.cta.title') }} />
          <p>{t('home.cta.desc')}</p>
          <button type="button" onClick={() => setShowForm(true)} className="btn-primary" style={{ border: 'none', cursor: 'pointer', fontSize: '1rem', padding: '16px 32px' }}>
            {t('home.cta.button')}
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer>
        <div className="footer-inner">
          <a href="/" className="logo" style={{ fontSize: '1rem' }} onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>elmakinatech</a>
          <div className="footer-links">
            <a href="mailto:atencionalcliente@elmakinatech.com">atencionalcliente@elmakinatech.com</a>
            <a href="/privacidad" onClick={(e) => { e.preventDefault(); setLegalModal('privacidad'); }}>{t('home.footer.privacy')}</a>
            <a href="/terminos" onClick={(e) => { e.preventDefault(); setLegalModal('terminos'); }}>{t('legal.terms.title')}</a>
            <a href="/politica-de-cookies" onClick={(e) => { e.preventDefault(); setLegalModal('cookies'); }}>{t('legal.cookies.title')}</a>
            <a href="/contacto" onClick={(e) => { e.preventDefault(); setLegalModal('contacto'); }}>{t('contact.title')}</a>
          </div>
          <p>{t('home.footer.copyright')}</p>
        </div>
      </footer>

      <CookieBanner />
    </>
  );
}

export default Landing;
